<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Guest" backTo="internal">
      <!-- Create New Guest Button -->
      <AppButton color="green" label="Create New Guest" @click="openCreateGuestPanel" :icon="true" class="mr-2">
        <template v-slot:icon>
          <Icon iconType="add" />
        </template>
      </AppButton>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <!-- Toolbar with Tabs and Search -->
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected" />
          </div>

          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <input
                  type="text"
                  class="ml-6 w-full lg:w-1/3 rounded-l bge-input bge-input-spacing"
                  placeholder="Search guest..."
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search" />
              </button>
            </div>
          </div>


        </div>

        <!-- Guest Table -->
        <TabContent :currentTab="currentTab">
          <template v-slot:[currentTab]>
            <DataTableComponent
                :columns="guest_columns"
                @row-click="onRowClick"
                :rowGroupMode="null"
                key="guest_list"
                :rowClass="rowClass"
            >
              <template v-slot:name-slot="{ rowData }">
                {{ rowData.last_name }}, {{ rowData.first_name }}
              </template>

              <template v-slot:status-slot="{ rowData }">
                  <span
                      :class="[
                      'expiry-badge',
                      rowData.is_active ? 'status-green' : 'status-red'
                    ]"
                  >
                    {{ rowData.is_active ? 'Active' : 'Inactive' }}
                  </span>
              </template>


              <!-- Action Buttons -->
              <template v-slot:action-slot="{ rowData }">
                <div class="flex items-center space-x-2">
                  <!-- Activate / Deactivate -->
                  <AppButton
                      :color="rowData.is_active ? 'red' : 'green'"
                      label=""
                      :icon="true"
                      @click.native.stop="toggleActiveStatus(rowData)"
                  >
                    <template #icon>
                      <Icon :iconType="rowData.is_active ? 'deactivate' : 'activate'" />
                    </template>
                  </AppButton>


                  <!-- Delete -->
                  <AppButton
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="deleteGuest(rowData.guest_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="delete" />
                    </template>
                  </AppButton>
                </div>
              </template>
            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>

    <!-- Create Guest Panel -->
    <portal to="overlay-outlet">
      <panel :showing="createGuestPanelOpen" @close="closeCreateGuestPanel" title="Create New Guest">
        <AddGuest />
      </panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Calendar from "primevue/calendar";
import Icon from "@/components/Icon.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/HR/Guest/Components/DataTableComponent.vue";
import Panel from "@/components/Panel.vue";
import AppButton from "@/components/Button.vue";
import AddGuest from "@/views/HR/Guest/Components/AddGuest.vue";
import { mapState, mapActions } from "vuex";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Calendar,
    Icon,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Panel,
    AppButton,
    AddGuest,
  },
  data() {
    return {
      tabs: [
        { id: "active", label: "Active Guests" },
        { id: "inactive", label: "Not Active Guests" },
      ],
      guest_columns: [
        {
          field: "name",
          header: "Name",
          sortable: true,
          custom: true,
          slotName: "name-slot",
          width: "20%",
        },
        {
          field: "company_name",
          header: "Company",
          sortable: true,
          custom: false,
          width: "20%",
        },
        {
          field: "postcode",
          header: "Postcode",
          sortable: true,
          custom: false,
          width: "10%",
        },
        {
          field: "email",
          header: "Email",
          sortable: true,
          custom: false,
          width: "20%",
        },
        {
          field: "telephone",
          header: "Telephone",
          sortable: true,
          custom: false,
          width: "15%",
        },
        {
          field: "is_active",
          header: "Status",
          sortable: true,
          custom: true,
          slotName: "status-slot",
          width: "10%",
        },
        {
          field: "action",
          header: "",
          sortable: false,
          custom: true,
          slotName: "action-slot",
          width: "10%",
        },
      ],
      createGuestPanelOpen: false,
    };
  },
  computed: {
    ...mapState("guest", ["currentTab", "searchText", "fromDate", "toDate"]),

    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("guest/UPDATE_SEARCH_TEXT", value);
      },
    },

    fromDateInput: {
      get() {
        const fromDate = this.fromDate;
        return fromDate ? new Date(fromDate) : null;
      },
      set(value) {
        this.$store.commit("guest/UPDATE_FROM_DATE", value);
      },
    },

    toDateInput: {
      get() {
        const toDate = this.toDate;
        return toDate ? new Date(toDate) : null;
      },
      set(value) {
        this.$store.commit("guest/UPDATE_TO_DATE", value);
      },
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("guest", [
      "fetchTableData",
      "updateSearchCriteria",
      "updateTab",
      "resetCurrentPage",
    ]),

    onTabSelected(newTab) {
      this.updateTab(newTab);
    },

    triggerSearch() {
      this.resetCurrentPage(0);
      this.updateTab(this.currentTab);
    },

    onRowClick(event) {
      this.$router.push({
        name: "guest-edit",
        params: { guest_id: event.data.guest_id },
      });
    },

    rowClass() {
      return "";
    },

    openCreateGuestPanel() {
      this.createGuestPanelOpen = true;
    },
    closeCreateGuestPanel() {
      this.createGuestPanelOpen = false;
    },

    // --- Delete Guest ---
    async deleteGuest(guestId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to delete this guest?",
        header: "Confirm Delete",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        await this.GuestService.deleteGuest(guestId);
        this.notifySuccess("Guest deleted successfully.");
        await this.fetchTableData();
      } catch (error) {
        console.error(error);
        this.notifyError("Couldn't delete the guest.");
      }
    },

    // --- Toggle Active / Inactive ---
    async toggleActiveStatus(rowData) {
      const action = rowData.is_active ? "deactivate" : "activate";
      const confirmation = await this.confirmAction({
        message: `Do you want to ${action} this guest?`,
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        const updateData = { is_active: !rowData.is_active };
        await this.GuestService.patchGuestStatus(rowData.guest_id, updateData);
        this.notifySuccess(`Guest ${action}d successfully.`);
        await this.fetchTableData();
      } catch (error) {
        console.error(error);
        this.notifyError(`Couldn't ${action} the guest.`);
      }
    },
  },
};
</script>

<style scoped>
.text-green-700 {
  color: #047857 !important;
}

.text-red-600 {
  color: #dc2626 !important;
}
.expiry-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 0.25rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-green {
  background-color: #dcfce7;
  color: #166534;
  border: 1px solid #22c55e;
}

.status-red {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #f87171;
}

</style>
